
import axios from 'axios';
import { ViewPaymentPlanCreate_RecipientRecordDocument, ViewPaymentPlanCreate_RecipientRecordQueryVariables, ViewPaymentPlanEdit_PaymentPlanRecordDocument, ViewPaymentPlanEdit_PaymentPlanRecordQueryVariables } from '@/generated/graphql';
import { mapGetters } from 'vuex';
import { addDays, differenceInDays } from 'date-fns';
import { defineComponent } from 'vue';
import { Payment_Plan_Conditions } from '@/schema/legal_entity/payment_plan/conditions';
import { deepClone } from '@/utils/index.js';
import { paymentPlanCancel } from '@/api/payment-plan';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';
import PaymentPromiseDialog from './components/PaymentPromiseDialog.vue';
import jsonEditorVue from '../components-demo/json-editor.vue';

export default defineComponent({
	name: 'EditPaymentPlan',
	components: {
		PaymentPromiseDialog
	},
	props: {
		recipient: { type: String, required: true, default: '' },
		paymentPlan: { type: String, required: true, default: '' }
	},
	apollo: {
		recipientRecord: {
			query: ViewPaymentPlanCreate_RecipientRecordDocument,
			variables(): ViewPaymentPlanCreate_RecipientRecordQueryVariables {
				return {
					'participant': this.selectedParticipantStore,
					'recipient': this.recipient
				};
			},
			update: data => data.risolto_recipient[0]
		},
		paymentPlanRecord: {
			query: ViewPaymentPlanEdit_PaymentPlanRecordDocument,
			variables(): ViewPaymentPlanEdit_PaymentPlanRecordQueryVariables {
				return {
					'participant': this.selectedParticipantStore,
					'paymentPlan': this.paymentPlan
				};
			},
			update: data => data.risolto_payment_plan[0]
		}
	},
	data() {
		return {
			editDialogVisible: false,
			cancelLoading: false,
			updatePaymentPromiseLoading: false,
			origSelectedInvoices: [],
			origSelectedCreditNotes: [],
			origSelectedDebitNotes: [],
			origSelectedPayments: [],
			edited_payment_promises: [],
			paymentPlanRecord: null,
			MIN_PERIOD: 2,
			MAX_PERIOD: 12,
			loading: false,
			form: {
				invoices: [],
				creditNotes: [],
				debitNotes: [],
				payments: [],
				conditions: {
					interest: 0,
					indemnification: 0,
					indemnificationMax: 0,
					indemnificationMin: 0,
					interestApplied: false,
					indemnificationApplied: false
				},
				constraints: {
					maxInstallments: 2
				}
			},
			rules: {}
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore']),
		totalAmount() {
			const invoicesAmount = this.form.invoices.reduce((sum, invoice) => {
				return sum + invoice.open_amount;
			}, 0.00);
			const creditNotesAmount = this.form.creditNotes.reduce((sum, creditNote) => {
				return sum + creditNote.open_amount;
			}, 0.00);
			const debitNotesAmount = this.form.debitNotes.reduce((sum, debitNote) => {
				return sum + debitNote.open_amount;
			}, 0.00);
			const paymentAmounts = this.form.payments.reduce((sum, payment) => {
				return sum + payment.open_amount;
			}, 0.00);

			return invoicesAmount - creditNotesAmount + debitNotesAmount - paymentAmounts;
		},
		totalInterest() {
			return this.form.invoices.reduce((sum, invoice) => {
				const openAmount = invoice.open_amount;
				const interestRate = (this.form.conditions.interest || 0.00) / 100;
				const interest = calculateInterest(openAmount, interestRate, invoice.due_date);
				return sum + interest;
			}, 0);
		},
		totalIndemnification() {
			return calculateIndemnification(
				this.totalAmount,
				(this.form.conditions.indemnification || 0.00) / 100,
				this.form.conditions.indemnificationMin,
				this.form.conditions.indemnificationMax
			);
		},
		totalWithConditions() {
			let totalWithConditions = this.totalAmount;
			if (this.form.conditions.interestApplied) {
				totalWithConditions += this.totalInterest;
			}
			if (this.form.conditions.indemnificationApplied) {
				totalWithConditions += this.totalIndemnification;
			}
			return totalWithConditions;
		},
		minAmount() {
			const minAmount = this.form.constraints.maxInstallments ? (this.totalWithConditions / this.form.constraints.maxInstallments) : 0;

			return parseFloat((Math.round(minAmount * 100) / 100).toFixed(2));
		},
		maxAmount() {
			const maxAmount = (this.totalWithConditions / this.MIN_PERIOD) || this.totalAmount.value;

			return parseFloat((Math.round(maxAmount * 100) / 100).toFixed(2));
		}
	},
	watch: {
		recipientRecord() {
			const payment_plan_conditions: Payment_Plan_Conditions = this.recipientRecord.legal_entity_by_reference.payment_plan_conditions;
			if (!payment_plan_conditions) {
				return;
			}

			this.form.conditions.interest = payment_plan_conditions.interest.rate_percentage;
			this.form.conditions.interestApplied = payment_plan_conditions.interest.enabled_by_default;
			this.form.conditions.indemnification = payment_plan_conditions.indemnification.percentage;
			this.form.conditions.indemnificationMin = payment_plan_conditions.indemnification.minimum_amount;
			this.form.conditions.indemnificationMax = payment_plan_conditions.indemnification.maximum_amount;
			this.form.conditions.indemnificationApplied = payment_plan_conditions.indemnification.enabled_by_default;
			this.MAX_PERIOD = payment_plan_conditions.maximum_duration ? payment_plan_conditions.maximum_duration.duration : 12;
		},
		paymentPlanRecord() {
		}
	},
	methods: {
		editHasLicenseAndPermission() {
			let required_license = 'CORE';
			let required_permission = 'UI_EDIT_PAYMENT_PLAN';
			return hasLicenseAndPermission(
				this.$store.state.risolto.licences[this.selectedParticipantStore],
				this.$store.state.risolto.permissions[this.selectedParticipantStore],
				required_license,
				required_permission
			);
		},
		showEditDocuments() {
			this.editDialogVisible = true;
			this.$nextTick(() => {
				this.preSelectElements();
			});
		},
		hideEditDocuments() {
			this.editDialogVisible = false;
		},
		handleCancelPaymentPlan(paymentPlan) {
			this.$confirm(this.$t('payment-plan.detail.are_you_sure'), this.$t('warning'), {
				confirmButtonText: this.$t('confirm'),
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			}).then(async () => {
				try {
					this.cancelLoading = true;
					await paymentPlanCancel(
						this.selectedParticipantStore,
						this.paymentPlanRecord.legal_entity,
						this.paymentPlanRecord.recipient,
						paymentPlan
					);

					this.$apollo.queries.paymentPlanRecord.refetch();
				} finally {
					this.cancelLoading = false;
				}
			}).catch(e => {

			});
		},
		async executeUpdatePaymentPlanDocuments() {
			this.loading = true;

			let invoices_to_add = this.form.invoices.filter((invoice) => {
				return !this.origSelectedInvoices.includes(invoice);
			});
			let invoices_to_remove = this.origSelectedInvoices.filter((invoice) => {
				return !this.form.invoices.includes(invoice);
			});
			let credit_notes_to_add = this.form.creditNotes.filter((credit_note) => {
				return !this.origSelectedCreditNotes.includes(credit_note);
			});
			let credit_notes_to_remove = this.origSelectedCreditNotes.filter((credit_note) => {
				return !this.form.creditNotes.includes(credit_note);
			});
			let debit_notes_to_add = this.form.debitNotes.filter((debit_notes) => {
				return !this.origSelectedDebitNotes.includes(debit_notes);
			});
			let debit_notes_to_remove = this.origSelectedDebitNotes.filter((debit_notes) => {
				return !this.form.debitNotes.includes(debit_notes);
			});
			let payments_to_add = this.form.payments.filter((payment) => {
				return !this.origSelectedPayments.includes(payment);
			});
			let payments_to_remove = this.origSelectedPayments.filter((payment) => {
				return !this.form.payments.includes(payment);
			});

			try {
				const requestPayload = {
					participant: this.selectedParticipantStore,
					legal_entity: this.recipientRecord.legal_entity_by_reference.reference,
					payment_plan: this.paymentPlanRecord.reference,
					invoices_to_add: invoices_to_add.map((invoice) => { return invoice.reference; }),
					invoices_to_remove: invoices_to_remove.map((invoice) => { return invoice.reference; }),
					credit_notes_to_add: credit_notes_to_add.map((creditNote) => { return creditNote.reference; }),
					credit_notes_to_remove: credit_notes_to_remove.map((creditNote) => { return creditNote.reference; }),
					debit_notes_to_add: debit_notes_to_add.map((debitNote) => { return debitNote.reference; }),
					debit_notes_to_remove: debit_notes_to_remove.map((debitNote) => { return debitNote.reference; }),
					payments_to_add: payments_to_add.map((payment) => { return payment.reference; }),
					payments_to_remove: payments_to_remove.map((payment) => { return payment.reference; }),
					prolong_payment_plan: false,
					new_installment_amount: {
						value: this.minAmount,
						currency: this.recipientRecord.currency
					}
				};

				let payment_plan_id = this.paymentPlanRecord.reference;
				const url = `https://${process.env.VUE_APP_API_ENDPOINT}/payment-plan/v1/payment-plan/${payment_plan_id}/invoices`;
				await axios.post(url, requestPayload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
			} catch (e) {
				this.loading = false;
			}
			this.loading = false;
		},
		async handleEditPaymentPromiseInstallmentAmount(paymentPromise) {
			// replace this with dialog (see template)
			const { amount, prolong } = await this.$refs.paymentPromiseDialog.show({
				amount: paymentPromise.total_amount,
				allowProlong: this.paymentPlanRecord.payment_promises.indexOf(paymentPromise) !== (this.paymentPlanRecord.payment_promises.length - 1)
			});

			if (amount !== null) {
				paymentPromise.total_amount = parseFloat(amount);
				await this.executeUpdatePaymentPromiseInstallmentAmount(paymentPromise, prolong);
				this.resetAll();
			}
		},
		async executeUpdatePaymentPromiseInstallmentAmount(paymentPromise, prolong) {
			this.updatePaymentPromiseLoading = true;

			try {
				let payment_plan_id = this.paymentPlanRecord.reference;
				const currency = paymentPromise.currency ?? 'EUR';
				const requestPayload = {
					participant: this.selectedParticipantStore,
					legal_entity: this.recipientRecord.legal_entity_by_reference.reference,
					payment_plan: payment_plan_id,
					payment_promise: paymentPromise.reference,
					new_installment_amount: {
						value: paymentPromise.total_amount,
						currency: currency
					},
					prolong_payment_plan: prolong
				};

				const url = `https://${process.env.VUE_APP_API_ENDPOINT}/payment-plan/v1/payment-plan/${payment_plan_id}/payment-promise/${paymentPromise.reference}}`;
				await axios.put(url, requestPayload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
			} catch (e) {
				if (e.response.data && e.response.data.message_code === 'payment_promise_cant_be_changed') {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('payment-plan.detail.edit.payment_promise_cant_be_changed'),
						type: 'error'
					});
				} else {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('payment-plan.detail.edit.generic_error'),
						type: 'error'
					});
				}
				this.updatePaymentPromiseLoading = false;
			}
			this.updatePaymentPromiseLoading = false;
		},
		preSelectElements() {
			if (!this.paymentPlanRecord && !this.recipientRecord) {
				return;
			}

			this.edited_payment_promises = deepClone(this.paymentPlanRecord.payment_promises);
			this.form.constraints.maxInstallments = this.paymentPlanRecord.payment_promises.length;
			this.form.conditions.interestApplied = this.paymentPlanRecord.conditions.interestApplied;
			this.form.conditions.indemnificationApplied = this.paymentPlanRecord.conditions.indemnificationApplied;
			this.origSelectedInvoices = [];
			this.origSelectedCreditNotes = [];
			this.origSelectedDebitNotes = [];
			this.origSelectedPayments = [];

			if (this.paymentPlanRecord.invoice_group_by_reference !== undefined && this.paymentPlanRecord.invoice_group_by_reference.invoice_group_invoices !== undefined) {
				if (this.paymentPlanRecord.invoice_group_by_reference.invoice_group_invoices.length > 0) {
					this.paymentPlanRecord.invoice_group_by_reference.invoice_group_invoices.forEach((invoice_group_entry) => {
						const invoice = this.recipientRecord.invoice_documents.find((invoice) => {
							return invoice.reference === invoice_group_entry.invoice_by_reference.reference;
						});
						if (invoice) {
							if (!this.$refs.invoicesEditTable.selection.includes(invoice)) {
								this.$refs.invoicesEditTable.toggleRowSelection(invoice);
							}
							this.origSelectedInvoices.push(invoice);
						}
					});
				}
			}

			if (this.paymentPlanRecord.invoice_group_by_reference !== undefined && this.paymentPlanRecord.invoice_group_by_reference.invoice_group_credit_notes !== undefined) {
				if (this.paymentPlanRecord.invoice_group_by_reference.invoice_group_credit_notes.length > 0) {
					this.paymentPlanRecord.invoice_group_by_reference.invoice_group_credit_notes.forEach((credit_note_group_entry) => {
						const credit_note = this.recipientRecord.credit_notes.find((credit_note) => {
							return credit_note.reference === credit_note_group_entry.credit_note_by_reference.reference;
						});
						if (credit_note) {
							if (!this.$refs.creditNotesTable.selection.includes(credit_note)) {
								this.$refs.creditNotesTable.toggleRowSelection(credit_note);
							}
							this.origSelectedCreditNotes.push(credit_note);
						}
					});
				}
			}

			if (this.paymentPlanRecord.invoice_group_by_reference !== undefined && this.paymentPlanRecord.invoice_group_by_reference.debit_note_group_debit_notes !== undefined) {
				if (this.paymentPlanRecord.invoice_group_by_reference.debit_note_group_debit_notes.length > 0) {
					this.paymentPlanRecord.invoice_group_by_reference.debit_note_group_debit_notes.forEach((debit_note_group_entry) => {
						const debit_note = this.recipientRecord.debit_notes.find((debit_note) => {
							return debit_note.reference === debit_note_group_entry.debit_note_by_reference.reference;
						});
						if (debit_note) {
							if (!this.$refs.debitNotesTable.selection.includes(debit_note)) {
								this.$refs.debitNotesTable.toggleRowSelection(debit_note);
							}
							this.origSelectedDebitNotes.push(debit_note);
						}
					});
				}
			}

			if (this.paymentPlanRecord.invoice_group_by_reference !== undefined && this.paymentPlanRecord.invoice_group_by_reference.invoice_group_payments !== undefined) {
				if (this.paymentPlanRecord.invoice_group_by_reference.invoice_group_payments.length > 0) {
					this.paymentPlanRecord.invoice_group_by_reference.invoice_group_payments.forEach((payment_group_entry) => {
						const payment = this.recipientRecord.payment_documents.find((payment) => {
							return payment.reference === payment_group_entry.payment_by_reference.reference;
						});
						if (payment) {
							if (!this.$refs.paymentsTable.selection.includes(payment)) {
								this.$refs.paymentsTable.toggleRowSelection(payment);
							}
							this.origSelectedPayments.push(payment);
						}
					});
				}
			}
		},
		goToRecipient() {
			this.$router.push({
				name: 'recipient-detail',
				params: {
					id: this.recipient,
					participant: this.selectedParticipantStore
				}
			});
		},
		handleGoBack() {
			this.$router.go(-1);
		},
		async resetAll() {
			await this.refreshRecipientRecord();
			await this.refreshPaymentPlanRecord();
			this.hideEditDocuments();
			this.preSelectElements();
		},
		async refreshRecipientRecord() {
			let variables: ViewPaymentPlanCreate_RecipientRecordQueryVariables = {
				participant: this.selectedParticipantStore,
				recipient: this.recipient
			};
			let result = await this.$apollo.query({
				query: ViewPaymentPlanCreate_RecipientRecordDocument,
				variables: variables
			});
			this.recipientRecord = result.data.risolto_recipient[0];
		},
		async refreshPaymentPlanRecord() {
			let variables: ViewPaymentPlanEdit_PaymentPlanRecordQueryVariables = {
				participant: this.selectedParticipantStore,
				paymentPlan: this.paymentPlan
			};
			let result = await this.$apollo.query({
				query: ViewPaymentPlanEdit_PaymentPlanRecordDocument,
				variables: variables
			});
			this.paymentPlanRecord = result.data.risolto_payment_plan[0];
		},
		handleInvoiceSelectionChange(selection) {
			this.form.invoices = selection;
		},
		handleCreditNoteSelectionChange(selection) {
			this.form.creditNotes = selection;
		},
		handleDebitNoteSelectionChange(selection) {
			this.form.debitNotes = selection;
		},
		handlePaymentSelectionChange(selection) {
			this.form.payments = selection;
		},
		async handleFormSubmit() {
			if (this.loading) {
				return;
			}

			this.$refs['paymentPlanForm'].validate((valid) => {
				if (valid) {
					this.formSubmit();
				} else {
					return false;
				}
			});
		},
		async handleFormCancel() {
			this.loading = false;
			await this.resetAll();
		},
		async formSubmit() {
			await this.executeUpdatePaymentPlanDocuments();
			await this.resetAll();
		}
	}
});

const DAYS_IN_YEAR = 365;

function getDaysOverdue(dueDateString) {
	const today = new Date(Date.now());
	const dueDate = new Date(dueDateString);
	const diffInDays = differenceInDays(today, dueDate);
	return diffInDays > 0 ? diffInDays : 0;
}

function calculateInterest(amount, yearlyInterestRate, dueDateString) {
	const daysOverdue = getDaysOverdue(dueDateString);
	const dailyInterestRate = yearlyInterestRate / DAYS_IN_YEAR;
	return amount * dailyInterestRate * daysOverdue;
}

function calculateIndemnification(amount, rate, min, max) {
	const indemnification = amount * rate;
	return Math.max(Math.min(indemnification, max), min);
}

